import { useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { ChatMessageRow } from './ChatMessageRow.js';
import { useChatNavigation } from './useChatNavigation.js';
import { useBan, useTimeout, useDeleteMessage } from '@/features/moderation/hooks';
import { ConfirmBanDialog } from '@/features/moderation/ConfirmBanDialog';
import { ConfirmDeleteDialog } from '@/features/moderation/ConfirmDeleteDialog';
import { TimeoutDialog } from '@/features/moderation/TimeoutDialog';
import type { ChatEvent, ChatMessage } from '@multimod/shared';
import type { ChatFeedStore } from './store';

/**
 * Componente-folha. NÃO abre WS. Recebe `store` via prop (criado pelo container
 * em PanelPage via useChatFeed). Isso garante 1 WS por live em produção (Bug A).
 */
export function ChatFeed({ store }: { store: ChatFeedStore }) {
  const messages = store((s) => s.messages);
  const filter = store((s) => s.filter);
  const pendingAction = store((s) => s.pendingAction);
  const setPendingAction = store((s) => s.setPendingAction);
  const selectedIndex = store((s) => s.selectedIndex);
  const { onKeyDown } = useChatNavigation(store);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Filtra antes de passar pro Virtuoso. Connection events sempre passam — mod
  // precisa ver reconnect/disconnect. Filtros aplicam só a msgs.
  // useMemo evita rodar o filter + criar ref nova de array em todo render do
  // PanelPage (que ocorre toda vez que viewerCount/title refetcha, ~30s).
  // Sem memo, Virtuoso recebe array novo e re-renderiza o virtualizador.
  const visibleMessages = useMemo(
    () =>
      messages.filter((ev) => {
        if (ev.type !== 'message') return true;
        if (filter.platform !== 'all' && ev.platform !== filter.platform) return false;
        if (filter.onlyMods && !(ev.isModerator || ev.isBroadcaster)) return false;
        return true;
      }),
    [messages, filter.platform, filter.onlyMods],
  );

  // Dialog state vive AQUI (fora do scope da virtualização Virtuoso).
  // Se vivesse em ChatMessageRow, reciclagem de row mata o useState e o dialog fecha.
  const [banFor, setBanFor] = useState<ChatMessage | null>(null);
  const [delFor, setDelFor] = useState<ChatMessage | null>(null);
  const [timeoutFor, setTimeoutFor] = useState<ChatMessage | null>(null);

  const ban = useBan(store);
  const timeout = useTimeout(store);
  const del = useDeleteMessage(store);

  // Bug C/D: purga marcas optimistic expiradas a cada 15s.
  // - bannedUsers: TTL 5min (reconcilia com desban externo)
  // - timedOutUsers: TTL = durationSeconds da chamada (badge some quando timeout natural acaba)
  // - optimisticBroadcasts: limpa metadados enviados/falhados após TTL defensivo
  useEffect(() => {
    const state = store.getState();
    const id = setInterval(() => {
      state.purgeExpiredBans();
      state.purgeExpiredTimeouts();
      state.purgeOptimisticBroadcasts();
    }, 15_000);
    return () => clearInterval(id);
  }, [store]);

  // Auto-focus no role=log ao montar e ao trocar de live. Sem isso, foco
  // fica em <body> (ou no button que disparou o atach, ex. "Atachar live
  // mock", "Conectar live") e hotkeys B/T/D são silenciosamente ignoradas
  // — o onKeyDown local só dispara quando o feed tem foco. Re-focar em
  // troca de store (= troca de live primary) recupera após remount/HMR
  // também. Só preserva se user estiver digitando.
  const feedRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const active = document.activeElement;
    const tag = active?.tagName?.toLowerCase();
    const isTyping = tag === 'input' || tag === 'textarea' || (active as HTMLElement | null)?.isContentEditable;
    if (isTyping) return;
    feedRef.current?.focus();
  }, [store]);

  // Hotkeys ↑/↓/B/T/D em window-level — sobrevivem ao foco escapar do
  // feed (click fora da UI, foco no body). Sem isso, mod que clica em
  // qualquer lugar fora do role=log perde nav até clicar de volta. Guard
  // ignora quando user está digitando (input/textarea/contenteditable).
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      // Quando foco está no role=log, o handler local (onKeyDown) já
      // processou e chamou preventDefault. Sem esse guard, ↑/↓ moveriam
      // selectedIndex em duas posições (local + global).
      if (e.defaultPrevented) return;
      // Usa document.activeElement em vez de e.target — quando o evento
      // sobe pra window, e.target é o ancestral que bubbla, não o input
      // focado de verdade.
      const active = document.activeElement as HTMLElement | null;
      const tag = active?.tagName?.toLowerCase();
      const isTyping = tag === 'input' || tag === 'textarea' || !!active?.isContentEditable;
      if (isTyping) return;
      const key = e.key;
      const isNav = key === 'ArrowDown' || key === 'ArrowUp';
      const isAction = ['b', 'B', 't', 'T', 'd', 'D'].includes(key);
      if (!isNav && !isAction) return;
      // Delega pro mesmo handler — synth event mínimo com target neutro
      // (div) pra não ser confundido com input no guard interno.
      onKeyDown({
        key,
        target: feedRef.current ?? document.createElement('div'),
        preventDefault: () => e.preventDefault(),
      } as unknown as React.KeyboardEvent<HTMLElement>);
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onKeyDown]);

  // Scroll segue a seleção: quando selectedIndex muda via ↑/↓, Virtuoso
  // garante que a row fica visível (com `behavior: 'auto'` pra não brigar
  // com prefers-reduced-motion — P16). 'center' mantém contexto acima e
  // abaixo da selecionada.
  useEffect(() => {
    if (selectedIndex === null || selectedIndex < 0) return;
    virtuosoRef.current?.scrollToIndex({
      index: selectedIndex,
      align: 'center',
      behavior: 'auto',
    });
  }, [selectedIndex]);

  // Hotkeys B/T/D escrevem pendingAction no store — aqui resolvemos msgKey→msg
  // e abrimos o dialog correspondente. Limpa pendingAction depois pra não re-disparar.
  useEffect(() => {
    if (!pendingAction) return;
    // #50: lookup O(1) via índice do store (antes: messages.find O(n) por teclada B/T/D).
    const msg = store.getState().getMessageByKey(pendingAction.msgKey);
    if (msg) {
      if (pendingAction.kind === 'ban') setBanFor(msg);
      else if (pendingAction.kind === 'delete') setDelFor(msg);
      else if (pendingAction.kind === 'timeout') setTimeoutFor(msg);
    }
    setPendingAction(null);
  }, [pendingAction, store, setPendingAction]);

  return (
    <>
      <div
        ref={feedRef}
        className="h-full mx-auto max-w-[1080px] focus-visible:outline-none"
        role="log"
        aria-live="polite"
        tabIndex={0}
        onKeyDown={onKeyDown}
      >
        <Virtuoso
          ref={virtuosoRef}
          data={visibleMessages}
          followOutput="auto"
          // Bug do auto-scroll que "trava" depois de enviar/receber (medido em
          // browser real, repro isolada): com `followOutput="auto"` puro, ao
          // inserir uma msg o Virtuoso scrolla pro fim usando a altura ESTIMADA
          // do item; o ResizeObserver mede a altura REAL (maior) logo depois e
          // sobra um gap. Com o `atBottomThreshold` default (4px) esse gap já
          // tira o estado `atBottom` → o follow trava em `false` e só volta com
          // scroll manual (exatamente o sintoma relatado). O envio é o gatilho
          // mais consistente porque a row "Você"/optimistic + reconcile mexem
          // mais na altura que uma msg recebida.
          //
          // `increaseViewportBy.bottom` pré-renderiza/mede os próximos itens
          // ANTES de entrarem no viewport, eliminando o gap de estimativa na
          // origem (robusto a qualquer altura — msgs longas, superchat, etc.).
          // `atBottomThreshold` modesto (~2 linhas) absorve só o ruído residual
          // de arredondamento subpixel sem puxar o mod quando ele scrolla pra
          // ler histórico (validado: scroll de 120px+ não é puxado pro fim).
          atBottomThreshold={60}
          increaseViewportBy={{ top: 0, bottom: 600 }}
          itemContent={(index: number, item: ChatEvent) => {
            if (item.type === 'message')
              return (
                <ChatMessageRow
                  msg={item}
                  store={store}
                  index={index}
                  onOpenBan={() => setBanFor(item)}
                  onOpenDelete={() => setDelFor(item)}
                  onOpenTimeout={() => setTimeoutFor(item)}
                />
              );
            if (item.type === 'connection') {
              return (
                <div className="px-3 py-1 text-xs text-fg-muted italic">
                  {item.subtype}{item.reason ? ` — ${item.reason}` : ''}
                </div>
              );
            }
            return null;
          }}
          style={{ height: '100%' }}
        />
      </div>

      {banFor && (
        <ConfirmBanDialog
          open
          msg={banFor}
          onOpenChange={(o) => !o && setBanFor(null)}
          onConfirm={(reason) => {
            ban.mutate({
              platform: banFor.platform,
              liveChatId: banFor.liveChatId,
              target: banFor.author,
              reason,
            });
            setBanFor(null);
          }}
        />
      )}
      {delFor && (
        <ConfirmDeleteDialog
          open
          msg={delFor}
          onOpenChange={(o) => !o && setDelFor(null)}
          onConfirm={() => {
            del.mutate({
              platform: delFor.platform,
              liveChatId: delFor.liveChatId,
              messageExternalId: delFor.externalId,
            });
            setDelFor(null);
          }}
        />
      )}
      {timeoutFor && (
        <TimeoutDialog
          open
          msg={timeoutFor}
          onOpenChange={(o) => !o && setTimeoutFor(null)}
          onConfirm={(seconds) => {
            timeout.mutate({
              platform: timeoutFor.platform,
              liveChatId: timeoutFor.liveChatId,
              target: timeoutFor.author,
              durationSeconds: seconds,
            });
            setTimeoutFor(null);
          }}
        />
      )}
    </>
  );
}
